import React, { useEffect, useState } from 'react'
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import { MuiThemeProvider, StylesProvider, Theme, createMuiTheme } from '@material-ui/core/styles'
import CssBaseline from '@material-ui/core/CssBaseline'

import { setAuthHeader } from 'utils'
import { useApiCall, useLogOut, useLogIn } from 'hooks'
import { Admin, TokenLoginResponse } from 'types'
import { tokenLogin } from 'api'
import { AdminContext, StyleContext } from 'contexts'
import { SnackbarContainer, snackbar, ProtectedRoutes } from 'components'
import { Style } from 'types/style'
import { useStyleContext } from 'contexts/StyleContext/useStyleContext'
import { ConfigUtils } from 'utils/config'

import { Login } from './Login'
import { Home } from './Home'
import { Main } from './Main'

const App: React.FC = () => {
  const [, setIsAppLoading] = useState(true)
  const [isThemeLoading, setIsThemeLoading] = useState(true)

  const styleContextValue = useStyleContext()

  const [admin, setAdmin] = useState<Admin | undefined>(() => {
    const localAdmin = localStorage.getItem('admin')
    if (!localAdmin) return undefined
    return JSON.parse(localAdmin)
  })

  const [tokenLoginApi] = useApiCall<void, TokenLoginResponse>(tokenLogin)

  // eslint-disable-next-line @typescript-eslint/member-delimiter-style
  const appConfig: Record<string, { title: string; favicon: string }> = {
    tea: {
      title: 'TEA Store',
      favicon: '/favicon-tea.ico',
    },
    ge: {
      title: 'GE Store',
      favicon: '/favicon-ge.ico',
    },
    cs: {
      title: 'Casa Sáenz Store',
      favicon: '/favicon-cs.ico',
    },
    'le-pain-quotidien': {
      title: 'Le Pain Quotidien',
      favicon: 'favicon-lpq.ico',
    },
    paru: {
      title: 'Club Paru',
      favicon: '/favicon-paru.ico',
    },
    jb: {
      title: 'Jeremy Button',
      favicon: '/favicon-jb.ico',
    },
    ts: {
      title: 'Tante Sara',
      favicon: '/favicon-ts.ico',
    },
    cm: {
      title: 'Club Mila',
      favicon: '/favicon-cm.ico',
    },
  }

  useEffect(() => {
    const appName = ConfigUtils.getAppName()

    document.title = appConfig[appName]?.title || 'App'
    const favicon = appConfig[appName]?.favicon || '/favicon-ge.ico'

    const faviconLink = document.querySelector("link[rel*='icon']") as HTMLLinkElement
    if (faviconLink) {
      faviconLink.href = favicon
    }
  }, [])

  const [logIn] = useLogIn()
  const [logOut] = useLogOut()

  useEffect(() => {
    const token = localStorage.getItem('token')
    if (!token) {
      setIsAppLoading(false)
      return
    }

    const updateAdmin = async () => {
      try {
        setAuthHeader(token)
        const { user: updatedAdmin } = await tokenLoginApi()
        logIn(token, updatedAdmin)
      } catch (_) {
        logOut()
      } finally {
        setIsAppLoading(false)
      }
    }
    updateAdmin()
  }, [logIn, logOut, tokenLoginApi])

  const [theme, setTheme] = useState<Theme>(createMuiTheme())

  const { style, fetchAndSetStyle, isLoadingStyle } = useStyleContext()

  useEffect(() => {
    const applyTheme = (styleData: Style) => {
      const { storeColors } = styleData
      const newTheme = createMuiTheme({
        palette: {
          primary: {
            main: storeColors.PRIMARY_MAIN, // COLORS.BASE
            contrastText: storeColors.PRIMARY_CONTRAST, // COLORS.WHITE,
          },
          secondary: {
            main: storeColors.SECONDARY_MAIN, // COLORS.ACCENT,
          },
          background: {
            default: storeColors.BACKGROUND,
          },
        },
      })
      setTheme(newTheme)
      setIsThemeLoading(false)
    }

    const updateTheme = async () => {
      if (style) {
        applyTheme(style)
      } else {
        await fetchAndSetStyle()
      }
    }

    updateTheme()
  }, [isLoadingStyle])

  return (
    <>
      {!isThemeLoading ? (
        <StylesProvider injectFirst>
          <StyleContext.Provider value={styleContextValue}>
            <MuiThemeProvider theme={theme}>
              <CssBaseline />
              <AdminContext.Provider value={{ admin, setAdmin }}>
                <Router>
                  <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route
                      path="/*"
                      element={
                        <ProtectedRoutes>
                          <Routes>
                            <Route path="/home" element={<Home />} />
                            <Route path="/*" element={<Main />} />
                          </Routes>
                        </ProtectedRoutes>
                      }
                    />
                  </Routes>
                </Router>
                <SnackbarContainer
                  ref={(ref) => {
                    snackbar.containerInstance = ref
                  }}
                />
              </AdminContext.Provider>
            </MuiThemeProvider>
          </StyleContext.Provider>
        </StylesProvider>
      ) : null}
    </>
  )
}

export { App }
